import * as THREE from 'three';

/**
 * Unified pointer event handling.
 * Raycasts ONLY against Layer 1 objects (interactive).
 * Dispatches custom events for building clicks.
 */
export class InputManager {
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();
  private camera: THREE.Camera;
  private scene: THREE.Scene;
  private enabled = true;
  private canvas: HTMLCanvasElement;
  private hoveredObject: THREE.Object3D | null = null;

  constructor(camera: THREE.Camera, scene: THREE.Scene, canvas: HTMLCanvasElement) {
    this.camera = camera;
    this.scene = scene;
    this.canvas = canvas;

    // Raycast only against layer 1
    this.raycaster.layers.set(1);

    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerdown', this.onPointerDown);
  }

  setCamera(camera: THREE.Camera): void {
    this.camera = camera;
  }

  setScene(scene: THREE.Scene): void {
    this.scene = scene;
  }

  enable(): void {
    this.enabled = true;
  }

  disable(): void {
    this.enabled = false;
    // Reset hover state
    if (this.hoveredObject) {
      this.canvas.style.cursor = 'default';
      this.hoveredObject = null;
    }
  }

  private onPointerMove = (event: PointerEvent): void => {
    if (!this.enabled) return;

    this.pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
    this.pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;

    this.raycaster.setFromCamera(this.pointer, this.camera);
    const intersects = this.raycaster.intersectObjects(this.scene.children, true);

    let foundInteractive: THREE.Object3D | null = null;
    for (const hit of intersects) {
      const obj = this.findInteractiveParent(hit.object);
      if (obj) {
        foundInteractive = obj;
        break;
      }
    }

    if (foundInteractive !== this.hoveredObject) {
      // Unhover previous
      if (this.hoveredObject) {
        window.dispatchEvent(new CustomEvent('object-unhover', { detail: { object: this.hoveredObject } }));
        this.canvas.style.cursor = 'default';
      }
      // Hover new
      if (foundInteractive) {
        window.dispatchEvent(new CustomEvent('object-hover', { detail: { object: foundInteractive } }));
        this.canvas.style.cursor = 'pointer';
      }
      this.hoveredObject = foundInteractive;
    }
  };

  private onPointerDown = (event: PointerEvent): void => {
    if (!this.enabled) return;
    if (event.button !== 0) return; // Left click only

    this.pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
    this.pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;

    this.raycaster.setFromCamera(this.pointer, this.camera);
    const intersects = this.raycaster.intersectObjects(this.scene.children, true);

    for (const hit of intersects) {
      const obj = this.findInteractiveParent(hit.object);
      if (obj) {
        const action = obj.userData.action as string | undefined;
        if (action) {
          window.dispatchEvent(new CustomEvent('object-click', { detail: { object: obj, action } }));
        }
        break;
      }
    }
  };

  /**
   * Walk up the parent chain to find an object with userData.interactive = true
   */
  private findInteractiveParent(obj: THREE.Object3D): THREE.Object3D | null {
    let current: THREE.Object3D | null = obj;
    while (current) {
      if (current.userData.interactive) return current;
      current = current.parent;
    }
    return null;
  }

  dispose(): void {
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
  }
}
